import { html, PropertyValues } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { ManzanoElement } from '@manzanohq/components/dist/internal/manzano-element.js';
import { watch } from '@manzanohq/components/dist/internal/watch.js';
import { Chart, registerables } from 'chart.js';
import type { ChartData, ChartOptions, ChartEvent, ActiveElement } from 'chart.js';
import type { MzChartType, ChartClickDetail } from './chart.types.js';
import styles from './chart.styles.js';

Chart.register(...registerables);

// Manzano palette colors for automatic dataset coloring
const PALETTE_KEYS = ['primary', 'danger', 'success', 'warning', 'neutral'] as const;

/**
 * @tag mz-chart
 * @summary Chart.js wrapper with Manzano theming, shadow DOM support, and responsive resize.
 *
 * @event mz-chart-click - Fires when a data point is clicked. Detail: ChartClickDetail
 */
export class MzChart extends ManzanoElement {
  static override styles = styles;

  /** The type of chart to render. */
  @property({ reflect: true }) type: MzChartType = 'bar';

  /** Chart.js data object — { labels, datasets }. */
  @property({ attribute: false }) data: ChartData = { labels: [], datasets: [] };

  /** Chart.js options object (merged with Manzano defaults). */
  @property({ attribute: false }) options: ChartOptions = {};

  @query('canvas') private canvas!: HTMLCanvasElement;
  @query('.chart-tooltip') private tooltipEl!: HTMLDivElement;

  @state() private tooltipVisible = false;

  private chart: Chart | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private themeObserver: MutationObserver | null = null;

  override firstUpdated(_changedProps: PropertyValues) {
    super.firstUpdated(_changedProps);
    this.createChart();
    this.setupResizeObserver();
    this.setupThemeObserver();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.destroyChart();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.themeObserver?.disconnect();
    this.themeObserver = null;
  }

  /** Returns the underlying Chart.js instance for advanced use cases. */
  getChartInstance(): Chart | null {
    return this.chart;
  }

  /** Force the chart to re-render. */
  updateChart() {
    if (this.chart) {
      this.chart.update();
    }
  }

  @watch('data')
  handleDataChange() {
    if (!this.chart) return;
    this.chart.data = this.applyThemeColors(this.data);
    this.chart.update();
  }

  @watch('type')
  handleTypeChange() {
    this.recreateChart();
  }

  @watch('options')
  handleOptionsChange() {
    this.recreateChart();
  }

  private readThemeColor(name: string): string {
    return getComputedStyle(this).getPropertyValue(`--mz-color-${name}`).trim();
  }

  private readThemeFont(): string {
    return getComputedStyle(this).getPropertyValue('--mz-font-sans').trim() || 'sans-serif';
  }

  /** Read palette colors from the current theme. */
  private getPaletteColors(): string[] {
    return PALETTE_KEYS.map(key => this.readThemeColor(key)).filter(Boolean);
  }

  /** Add alpha to a hex or CSS color string. */
  private withAlpha(color: string, alpha: number): string {
    // Handle hex colors
    const hex = color.replace(/^#/, '');
    if (/^[0-9a-f]{6}$/i.test(hex)) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    return color;
  }

  /**
   * Apply Manzano theme colors to datasets that don't already have user-specified colors.
   * Returns a deep-ish copy so we don't mutate the user's original data object.
   */
  private applyThemeColors(data: ChartData): ChartData {
    const palette = this.getPaletteColors();
    if (palette.length === 0) return data;

    const surfaceColor = this.readThemeColor('surface') || '#ffffff';
    const isPieType = this.type === 'pie' || this.type === 'doughnut' || this.type === 'polarArea';
    const isLineType = this.type === 'line' || this.type === 'radar';

    const datasets = (data.datasets ?? []).map((ds, i) => {
      const themed = { ...ds };

      if (isPieType) {
        // Pie/doughnut/polarArea: each segment gets a color from the palette
        if (!ds.backgroundColor) {
          const numPoints = Array.isArray(ds.data) ? ds.data.length : 0;
          themed.backgroundColor = Array.from({ length: numPoints }, (_, j) => palette[j % palette.length]);
        }
        if (!ds.borderColor) {
          themed.borderColor = surfaceColor;
        }
        if ((ds as unknown as Record<string, unknown>).borderWidth === undefined) {
          (themed as unknown as Record<string, unknown>).borderWidth = 2;
        }
      } else if (isLineType) {
        // Line/radar: border color from palette, transparent fill
        const color = palette[i % palette.length];
        if (!ds.borderColor) {
          themed.borderColor = color;
        }
        if (!ds.backgroundColor) {
          themed.backgroundColor = this.withAlpha(color, 0.12);
        }
        if ((ds as unknown as Record<string, unknown>).pointBackgroundColor === undefined) {
          (themed as unknown as Record<string, unknown>).pointBackgroundColor = color;
        }
      } else {
        // Bar/scatter/bubble: solid color from palette
        const color = palette[i % palette.length];
        if (!ds.backgroundColor) {
          themed.backgroundColor = color;
        }
        if (!ds.borderColor) {
          themed.borderColor = color;
        }
      }

      return themed;
    });

    return { ...data, datasets };
  }

  /** Build the merged Chart.js options with Manzano defaults. */
  private buildOptions(): ChartOptions {
    const textColor = this.readThemeColor('text') || '#1c1915';
    const textSecondary = this.readThemeColor('text-secondary') || '#79726b';
    const borderColor = this.readThemeColor('border') || '#d5d1cb';
    const fontFamily = this.readThemeFont();

    const defaults: ChartOptions = {
      responsive: true,
      maintainAspectRatio: true,
      animation: {
        duration: 300,
        easing: 'easeOutQuart'
      },
      font: {
        family: fontFamily,
        size: 12
      },
      color: textSecondary,
      plugins: {
        legend: {
          labels: {
            color: textColor,
            font: { family: fontFamily },
            boxWidth: 10,
            boxHeight: 10,
            useBorderRadius: true,
            borderRadius: 2,
            padding: 16,
            generateLabels: (chart) => {
              const defaults = Chart.defaults.plugins.legend.labels.generateLabels!(chart);
              return defaults.map(label => ({
                ...label,
                fillStyle: label.strokeStyle || label.fillStyle
              }));
            }
          }
        },
        tooltip: {
          enabled: false,
          external: (context) => this.handleExternalTooltip(context)
        }
      },
      scales: this.type === 'pie' || this.type === 'doughnut'
        ? {}
        : this.type === 'radar' || this.type === 'polarArea'
        ? {
          r: {
            ticks: {
              color: textSecondary,
              font: { family: fontFamily },
              backdropColor: 'transparent'
            },
            grid: { color: borderColor },
            angleLines: { color: borderColor },
            pointLabels: { color: textSecondary, font: { family: fontFamily } }
          }
        }
        : {
          x: {
            ticks: { color: textSecondary, font: { family: fontFamily } },
            grid: { color: borderColor },
            border: { color: borderColor }
          },
          y: {
            ticks: { color: textSecondary, font: { family: fontFamily } },
            grid: { color: borderColor },
            border: { color: borderColor }
          }
        },
      onClick: (_event: ChartEvent, elements: ActiveElement[]) => {
        if (elements.length === 0) return;
        const el = elements[0];
        const dataset = this.chart?.data.datasets[el.datasetIndex];
        const value = Array.isArray(dataset?.data) ? dataset.data[el.index] : undefined;
        const labels = this.chart?.data.labels;
        const label = Array.isArray(labels) ? String(labels[el.index] ?? '') : '';

        this.emit<ChartClickDetail>('mz-chart-click', {
          detail: {
            datasetIndex: el.datasetIndex,
            index: el.index,
            value: typeof value === 'number' ? value : (value as unknown as Record<string, number>)?.y ?? 0,
            label
          }
        });
      }
    };

    return this.deepMerge(defaults, this.options);
  }

  /** Simple deep merge of two objects (b overrides a). */
  private deepMerge(a: Record<string, unknown>, b: Record<string, unknown>): Record<string, unknown> {
    const result: Record<string, unknown> = { ...a };
    for (const key of Object.keys(b)) {
      if (
        b[key] !== null &&
        typeof b[key] === 'object' &&
        !Array.isArray(b[key]) &&
        a[key] !== null &&
        typeof a[key] === 'object' &&
        !Array.isArray(a[key])
      ) {
        result[key] = this.deepMerge(a[key] as Record<string, unknown>, b[key] as Record<string, unknown>);
      } else {
        result[key] = b[key];
      }
    }
    return result;
  }

  /** Custom tooltip rendered inside shadow DOM. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleExternalTooltip(context: { tooltip: any }) {
    const { tooltip } = context;

    if (tooltip.opacity === 0) {
      this.tooltipVisible = false;
      return;
    }

    // Position
    const tooltipEl = this.tooltipEl;
    if (!tooltipEl) return;

    tooltipEl.style.left = `${tooltip.caretX}px`;
    tooltipEl.style.top = `${tooltip.caretY}px`;
    tooltipEl.style.transform = 'translate(-50%, calc(-100% - 8px))';

    // Build content
    let titleHtml = '';
    if (tooltip.title?.length) {
      titleHtml = `<div class="chart-tooltip__title">${tooltip.title.join(', ')}</div>`;
    }

    const bodyLines: string[][] = tooltip.body?.map((b: { lines: string[] }) => b.lines) ?? [];
    const colors: { backgroundColor: string }[] = tooltip.labelColors ?? [];
    const itemsHtml = bodyLines.map((lines: string[], i: number) => {
      const color = colors[i]?.backgroundColor ?? '#888';
      const bgStr = typeof color === 'object' ? '#888' : String(color);
      return lines.map((line: string) =>
        `<div class="chart-tooltip__item">
          <span class="chart-tooltip__swatch" style="background:${bgStr}"></span>
          <span>${line}</span>
        </div>`
      ).join('');
    }).join('');

    tooltipEl.innerHTML = `<div class="chart-tooltip__inner">${titleHtml}${itemsHtml}</div>`;
    this.tooltipVisible = true;
  }

  private createChart() {
    if (!this.canvas) return;

    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.chart = new Chart(ctx, {
      type: this.type,
      data: this.applyThemeColors(this.data),
      options: this.buildOptions()
    } as any);
  }

  private destroyChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  private recreateChart() {
    this.destroyChart();
    this.createChart();
  }

  private setupResizeObserver() {
    this.resizeObserver = new ResizeObserver(() => {
      this.chart?.resize();
    });
    this.resizeObserver.observe(this);
  }

  private setupThemeObserver() {
    this.themeObserver = new MutationObserver(() => {
      this.recreateChart();
    });
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });
  }

  override render() {
    return html`
      <div class="chart-container">
        <canvas></canvas>
        <div class="chart-tooltip ${this.tooltipVisible ? 'visible' : ''}"></div>
      </div>
    `;
  }
}
